import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Warning, CurrencyInr, SealCheck, CheckCircle } from "@phosphor-icons/react";
import api from "@/lib/api";
import { inr, JOB_STATUS_LABELS, JOB_STATUS_COLORS, EMPANELMENT_STATUS } from "@/lib/constants";
import { PageHeader, SectionTitle, EmptyState, Stat } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";

export default function Alerts() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/alerts").then((r) => setData(r.data)).catch(() => setData({ sla: [], payments_overdue: [], empanelment: [] })); }, []);

  if (!data) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Checking for red flags…</div>;

  const clean = !data.sla.length && !data.payments_overdue.length && !data.empanelment.length;

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <PageHeader overline="Dashboard" title="Alerts & Notifications" subtitle="Everything that needs attention today, in one panel" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat label="SLA at risk / breached" value={data.sla.length} tone={data.sla.length ? "text-rose-600" : "text-emerald-600"} testid="stat-sla" />
        <Stat label="Payments overdue" value={data.payments_overdue.length} tone={data.payments_overdue.length ? "text-amber-600" : "text-emerald-600"} testid="stat-overdue" />
        <Stat label="Empanelments expiring" value={data.empanelment.length} tone={data.empanelment.length ? "text-indigo-600" : "text-emerald-600"} testid="stat-empanel" />
      </div>

      {clean && <EmptyState icon={CheckCircle}>Nothing needs your attention right now. All deadlines, payments and empanelments are on track.</EmptyState>}

      {data.sla.length > 0 && (
        <Card className="p-6 border-border" data-testid="sla-panel">
          <SectionTitle icon={Warning}>Delivery Deadlines</SectionTitle>
          <div className="space-y-2">
            {data.sla.map((j) => (
              <div key={j.id} className={`flex flex-wrap items-center justify-between gap-2 p-3 rounded-md border ${j.severity === "breached" ? "bg-rose-50 border-rose-100" : "bg-amber-50 border-amber-100"}`} data-testid={`sla-${j.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm text-primary truncate">{j.applicant_name} · {j.bank_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{j.reference} · due {(j.deadline || "").slice(0, 10)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold uppercase tracking-wide ${j.severity === "breached" ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                    {j.severity === "breached" ? "Past due" : "Due soon"}
                  </span>
                  <span className={`text-xs px-2 py-0.5 rounded border ${JOB_STATUS_COLORS[j.status]}`}>{JOB_STATUS_LABELS[j.status]}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {data.payments_overdue.length > 0 && (
        <Card className="p-6 border-border" data-testid="payment-panel">
          <SectionTitle icon={CurrencyInr} right={<Link to="/app/invoices/outstanding" className="text-xs text-accent hover:underline">Open collections view</Link>}>Payments Overdue</SectionTitle>
          <div className="space-y-2">
            {data.payments_overdue.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md bg-muted border border-border" data-testid={`overdue-${i.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm text-primary truncate">{i.bill_to_name}</div>
                  <div className="text-xs text-muted-foreground font-mono">{i.invoice_number} · {i.days_overdue} days overdue</div>
                </div>
                <div className="font-display font-bold text-primary tabular">{inr((i.total || 0) - (i.amount_paid || 0))}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {data.empanelment.length > 0 && (
        <Card className="p-6 border-border" data-testid="empanelment-panel">
          <SectionTitle icon={SealCheck} right={<Link to="/app/branches/empanelment" className="text-xs text-accent hover:underline">Open tracker</Link>}>Empanelment Renewals</SectionTitle>
          <div className="space-y-2">
            {data.empanelment.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 p-3 rounded-md bg-muted border border-border" data-testid={`empanel-${b.id}`}>
                <div className="min-w-0">
                  <Link to={`/app/branches/${b.id}`} className="font-medium text-sm text-primary hover:text-accent truncate block">{b.bank_name} — {b.branch_name}</Link>
                  <div className="text-xs text-muted-foreground font-mono">Renewal due {(b.empanelment_renewal_due || "").slice(0, 10)} · {EMPANELMENT_STATUS[b.empanelment_status] || "—"}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded border font-semibold uppercase tracking-wide ${b.severity === "expired" ? "bg-rose-100 text-rose-700 border-rose-200" : "bg-indigo-100 text-indigo-800 border-indigo-200"}`}>
                  {b.severity === "expired" ? "Expired" : "Expiring"}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
